import {
  CreateJobPositionFromFileMutation,
  CreateJobPositionFromFileMutationVariables,
  useCreateJobPositionFromFileMutation,
} from "@graphql/generated";
import { useSearchParams } from "react-router-dom";

import { handleApiRequest } from "@cpj-ai/panel-npm-package";
import { useOrganizationStore } from "@store/useOrganizationStore";
import { useState } from "react";
import toast from "react-hot-toast";

const convertBlobToWebm = async (blob: Blob): Promise<Blob> => {
  // Only convert audio files, return other file types (PDF, images, etc.) as-is
  if (!blob.type.startsWith("audio/")) {
    return blob;
  }

  if (blob.type === "audio/webm" || blob.type === "audio/webm;codecs=opus") {
    return blob;
  }

  const webmMimeType = "audio/webm;codecs=opus";
  if (!MediaRecorder.isTypeSupported(webmMimeType)) {
    console.warn("WebM format not supported, sending original format");
    return blob;
  }

  try {
    const audioContext = new AudioContext();
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const destination = audioContext.createMediaStreamDestination();
    const source = audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(destination);

    const mediaRecorder = new MediaRecorder(destination.stream, {
      mimeType: webmMimeType,
    });

    const chunks: Blob[] = [];

    return new Promise((resolve) => {
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const webmBlob = new Blob(chunks, { type: webmMimeType });
        audioContext.close();
        resolve(webmBlob);
      };

      mediaRecorder.start();
      source.start(0);

      setTimeout(
        () => {
          mediaRecorder.stop();
          source.stop();
        },
        audioBuffer.duration * 1000 + 100,
      );
    });
  } catch (error) {
    console.error("Error converting audio to webm:", error);

    return new Blob([blob], { type: "audio/webm" });
  }
};

interface IAiJobPositionCreatorProps {
  handleNextStep: () => void;
}

export const useAiJobPositionCreator = ({ handleNextStep }: IAiJobPositionCreatorProps) => {
  const [, setSearchParams] = useSearchParams();
  const [createJobPositionFromFile] = useCreateJobPositionFromFileMutation();
  const { selectedOrganization } = useOrganizationStore();
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleAnalyzeWithAI = async (file: Blob) => {
    setIsAnalyzing(true);

    const webmFile = await convertBlobToWebm(file);

    await handleApiRequest<CreateJobPositionFromFileMutation, CreateJobPositionFromFileMutationVariables>({
      apiFunction: createJobPositionFromFile,
      variables: {
        file: webmFile,
        organizationId: selectedOrganization?.id ?? "",
      },
      componentName: "AiJobPositionCreator",
      actionName: "createJobPositionFromFile",
      displaySuccessToast: false,
      onSuccess: (data) => {
        const jobPositionId = data?.data?.account?.organization?.createJobPositionFromFile?.jobPosition?.id;

        if (jobPositionId) {
          toast.success("Job post created successfully.");
          setSearchParams({ id: jobPositionId });
          handleNextStep();
        } else {
          toast.error("Failed to create job post. Please try again.");
          setIsAnalyzing(false);
        }
      },
      onError: () => {
        toast.error("Failed to create job post. Please try again.");
        setIsAnalyzing(false);
      },
    });
  };

  return { handleAnalyzeWithAI, isAnalyzing };
};
